// Enhanced FileList Component with improved accessibility
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import FileItem from './FileItem';
import { ANIMATIONS } from '../utils/constants';

/**
 * Accessible component to display the list of files with their upload status
 * - Improved list semantics for screen readers
 * - Enhanced keyboard navigation
 * - Better status announcements
 *
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of file objects
 * @param {Object} props.uploadProgress - Object mapping file IDs to upload progress
 * @param {string} props.currentUploadingId - ID of the currently uploading file
 * @param {boolean} props.isUploading - Whether files are currently uploading
 * @param {boolean} props.isComplete - Whether upload is complete
 * @param {Function} props.onRemoveFile - Function to remove a file
 * @param {Function} props.onRetryUpload - Function to retry a failed upload
 * @param {Object} props.fileListRef - Ref for the file list container
 * @param {Object} props.fileItemRefs - Refs for individual file items
 * @returns {JSX.Element} Accessible file list component
 */
const FileList = ({
  files,
  uploadProgress,
  currentUploadingId,
  isUploading,
  isComplete,
  onRemoveFile,
  onRetryUpload,
  fileListRef,
  fileItemRefs,
}) => {
  if (!files.length) {
    return null;
  }

  // Calculate summary statistics for screen readers
  const totalFiles = files.length;
  const uploadedFiles = Object.values(uploadProgress).filter(progress => progress === 100).length;
  const failedFiles = Object.values(uploadProgress).filter(progress => progress === -1).length;
  const pendingFiles = totalFiles - uploadedFiles - failedFiles - (currentUploadingId ? 1 : 0);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="file-list-container fs-6"
        style={{ width: '100%' }}>
        {/* Status summary for screen readers */}
        <div className="visually-hidden font-size-medium" aria-live="polite">
          {isUploading
            ? `Uploading files. ${uploadedFiles} of ${totalFiles} complete. ${failedFiles} failed. ${pendingFiles} pending.`
            : isComplete
              ? `Upload complete. ${uploadedFiles} of ${totalFiles} files uploaded successfully.`
              : `${totalFiles} files selected for upload.`}
        </div>

        <div
          ref={fileListRef}
          className="pe-2 file-list"
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            width: '100%',
          }}
          role="list"
          aria-label="Selected files"
          tabIndex="0">
          <motion.div variants={ANIMATIONS.containerVariants} initial="hidden" animate="visible">
            <div className="d-flex flex-column gap-2 fs-6">
              {files.map(file => (
                <FileItem
                  key={file.id}
                  file={file}
                  progress={uploadProgress[file.id]}
                  isCurrentlyUploading={currentUploadingId === file.id}
                  isUploading={isUploading}
                  isComplete={isComplete}
                  onRemove={() => onRemoveFile(file.id)}
                  onRetry={() => onRetryUpload(file)}
                  ref={el => (fileItemRefs.current[file.id] = el)}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Navigation instructions for keyboard users */}
        <div className="visually-hidden font-size-small">
          Use arrow keys to navigate through files. Press Enter on remove or retry buttons to
          activate.
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FileList;
